import { isSupportedLocale, resolveLocale, type Locale } from '@shared/locales';

export { requestedLanguage } from '@shared/locales';

/**
 * Browser-language detection for the UI language, run only from a client-side
 * effect (see I18nBootstrap) — never during the first render of an unprefixed
 * English URL, which must match the prerendered English HTML exactly.
 * Deliberately not `i18next-browser-languagedetector`'s navigator plugin: that
 * would run during i18next's init and pick Spanish before hydration, which is
 * the mismatch this app avoids.
 *
 * The effect does not swap translations in place. It navigates to the
 * matching prefixed URL (`/es/…`) so the prerendered page for that language
 * is what loads. A prefixed URL never reaches this detector: the path
 * already named its language.
 *
 * `navigator.languages` is the preference order, best first, with no quality
 * values. The first tag whose language prefix we serve wins; anything else
 * is English.
 */
export function detectBrowserLanguage(): Locale {
  if (typeof navigator === 'undefined') return resolveLocale(undefined);

  const candidates = navigator.languages && navigator.languages.length > 0 ? navigator.languages : [navigator.language];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const locale = resolveLocale(candidate);
    // resolveLocale falls back to the default for unknown tags; only treat
    // a candidate as a hit when its base language is actually supported.
    const base = candidate.split('-')[0]?.toLowerCase();
    if (isSupportedLocale(base)) return locale;
  }
  return resolveLocale(undefined);
}
